import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { useNotify } from "react-admin";
import { MASKED_VALUE, OrgConfigResponse } from "@arrowsup/platform-dtos";
import { OrgConfigService } from "../../logic/org-config/org-config.service";
import { OauthService } from "../../logic/integrations/oauth/oauth.service";
import { OauthProvider } from "@arrowsup/platform-dtos";
import { AppConfigService } from "../../logic/app-config/use-app-config.service";
import { IntegrationConfigsLayout } from "../integrations/integrations-config-layout";

interface Props {
  orgConfigService: OrgConfigService;
  oauthServices: Record<OauthProvider, OauthService>;
  acs: AppConfigService;
}

export const OrgConfigPage: React.FC<Props> = ({
  orgConfigService,
  oauthServices,
  acs,
}) => {
  const notify = useNotify();
  const [config, setConfig] = useState<OrgConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Cloudflare form state
  const [cfNamespace, setCfNamespace] = useState("");
  const [cfAccountId, setCfAccountId] = useState("");
  const [cfApiToken, setCfApiToken] = useState("");
  const [cfApiTokenTouched, setCfApiTokenTouched] = useState(false);
  const [cfSaving, setCfSaving] = useState(false);

  // Twilio form state
  const [twilioAccountSid, setTwilioAccountSid] = useState("");
  const [twilioAuthToken, setTwilioAuthToken] = useState("");
  const [twilioAuthTokenTouched, setTwilioAuthTokenTouched] = useState(false);
  const [twilioSaving, setTwilioSaving] = useState(false);

  // Token state
  const [newToken, setNewToken] = useState<string | null>(null);
  const [tokenRefreshing, setTokenRefreshing] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orgConfigService.getConfig();
      setConfig(data);
      setCfNamespace(data.cloudflare.landingPageNamespace);
      setCfAccountId(data.cloudflare.accountId);
      setTwilioAccountSid(data.twilio?.accountSid ?? "");
    } catch (e) {
      notify("Failed to load configuration", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [orgConfigService, notify]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleCloudflareSave = async () => {
    setCfSaving(true);
    try {
      const updated = await orgConfigService.updateConfig({
        cloudflare: {
          landingPageNamespace: cfNamespace,
          accountId: cfAccountId,
          ...(cfApiToken ? { apiToken: cfApiToken } : {}),
        },
      });
      setConfig(updated);
      setCfApiToken("");
      setCfApiTokenTouched(false);
    } catch {
      notify("Failed to save Cloudflare configuration", { type: "error" });
    } finally {
      setCfSaving(false);
    }
  };

  const handleTwilioSave = async () => {
    setTwilioSaving(true);
    try {
      const updated = await orgConfigService.updateConfig({
        twilio: {
          accountSid: twilioAccountSid,
          ...(twilioAuthToken ? { authToken: twilioAuthToken } : {}),
        },
      });
      setConfig(updated);
      setTwilioAuthToken("");
      setTwilioAuthTokenTouched(false);
    } catch {
      notify("Failed to save Twilio configuration", { type: "error" });
    } finally {
      setTwilioSaving(false);
    }
  };

  const handleRefreshToken = async () => {
    setTokenRefreshing(true);
    try {
      const result = await orgConfigService.refreshToken();
      setNewToken(result.token);
      setConfig((prev) => (prev ? { ...prev, orgTokenExists: true } : prev));
    } catch {
      notify("Failed to refresh token", { type: "error" });
    } finally {
      setTokenRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Box p={2}>
        <Alert severity="error">Failed to load config</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: "0 auto", p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ textAlign: "center" }}>
        Organization Configuration
      </Typography>

      {/* General */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="General" />
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Timezone"
              value={config.general.tz}
              fullWidth
              InputProps={{ readOnly: true }}
              disabled={true}
            />
            <TextField
              label="ID"
              value={config.general.orgId}
              fullWidth
              InputProps={{ readOnly: true }}
              disabled={true}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Cloudflare */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Cloudflare" />
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Landing Page Namespace"
              value={cfNamespace}
              onChange={(e) => setCfNamespace(e.target.value)}
              fullWidth
            />
            <TextField
              label="Account ID"
              value={cfAccountId}
              onChange={(e) => setCfAccountId(e.target.value)}
              fullWidth
            />
            <TextField
              label="API Token"
              value={
                !cfApiTokenTouched && config.cloudflare.apiTokenExists
                  ? MASKED_VALUE
                  : cfApiToken
              }
              onFocus={() => setCfApiTokenTouched(true)}
              onBlur={() => {
                if (!cfApiToken) setCfApiTokenTouched(false);
              }}
              onChange={(e) => setCfApiToken(e.target.value)}
              placeholder="Enter new API token (write-only)"
              fullWidth
              type="password"
            />
            <Box>
              <Button
                variant="contained"
                onClick={() => void handleCloudflareSave()}
                disabled={cfSaving}
              >
                {cfSaving ? "Saving..." : "Save Cloudflare"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Organization Token */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Organization Token" />
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Token"
              value={newToken ?? (config.orgTokenExists ? MASKED_VALUE : "")}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            {!config.orgTokenExists && !newToken && (
              <Alert severity="info">
                No organization token exists. Generate one below.
              </Alert>
            )}
            {config.orgTokenExists && !newToken && (
              <Alert severity="warning">
                Generating a new token will invalidate the existing token.
              </Alert>
            )}
            {newToken && (
              <Alert severity="warning">
                Copy this token now. It will not be shown again.
              </Alert>
            )}
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                onClick={() => void handleRefreshToken()}
                disabled={tokenRefreshing}
              >
                {tokenRefreshing ? "Refreshing..." : "Generate New Token"}
              </Button>
              {newToken && (
                <Button
                  variant="outlined"
                  onClick={() => void navigator.clipboard.writeText(newToken)}
                >
                  Copy
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Twilio */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Twilio" />
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Account SID"
              value={twilioAccountSid}
              onChange={(e) => setTwilioAccountSid(e.target.value)}
              fullWidth
            />
            <TextField
              label="Auth Token"
              value={
                !twilioAuthTokenTouched && config.twilio?.authTokenExists
                  ? MASKED_VALUE
                  : twilioAuthToken
              }
              onFocus={() => setTwilioAuthTokenTouched(true)}
              onBlur={() => {
                if (!twilioAuthToken) setTwilioAuthTokenTouched(false);
              }}
              onChange={(e) => setTwilioAuthToken(e.target.value)}
              placeholder="Enter new auth token (write-only)"
              fullWidth
              type="password"
            />
            {config.twilio === null && !twilioAccountSid && (
              <Alert severity="info">
                Twilio is not configured for this organization.
              </Alert>
            )}
            <Box>
              <Button
                variant="contained"
                onClick={() => void handleTwilioSave()}
                disabled={twilioSaving}
              >
                {twilioSaving ? "Saving..." : "Save Twilio"}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Integrations */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Integrations" />
        <CardContent>
          <React.Suspense
            fallback={
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            }
          >
            <IntegrationConfigsLayout oauthServices={oauthServices} acs={acs} />
          </React.Suspense>
        </CardContent>
      </Card>
    </Box>
  );
};
